import React, { useCallback, useEffect, useRef } from "react";
import { Route, useHistory } from "react-router-dom";

import CloseIcon from "@mui/icons-material/Close";
import { Box, IconButton, Slide, Stack } from "@mui/material";
import { FileMetadata, Notes } from "components";
import { Document } from "components/Documents";
import { Form } from "components/Form";
import { Routes } from "constants/routes";

interface Props {
  open: boolean;
}

export const WorkspaceContentModal: React.FC<Props> = ({ open }) => {
  const history = useHistory();
  // Track the last location when modal was closed, so we can return there
  const returnLocationRef = useRef<string | null>(null);

  useEffect(() => {
    if (!open) {
      // While modal is closed, keep updating the return location
      returnLocationRef.current = history.location.pathname + history.location.search;
    }
  }, [open, history.location.pathname, history.location.search]);

  const goBack = useCallback(() => {
    const returnTo = returnLocationRef.current;
    if (returnTo) {
      // Replace instead of push so the modal route is removed from history —
      // otherwise browser back would reopen the modal.
      history.replace(returnTo);
    } else {
      // Page was reloaded on a modal route — no previous location captured.
      // Fall back to the workspace root by stripping the item segment
      // e.g. /main/deal/123/workspace/form/456 → /main/deal/123/workspace
      const workspacePath = history.location.pathname.replace(/\/workspace\/.*/, "/workspace");
      history.replace(workspacePath);
    }
  }, [history]);

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        goBack();
      }
    };
    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [goBack]);

  return (
    <Slide direction="up" in={open} mountOnEnter unmountOnExit timeout={{ enter: 500, exit: 0 }}>
      <Box
        sx={{
          position: "fixed",
          inset: 0,
          zIndex: 1300,
          backgroundColor: "#f7f9fc",
          display: "flex",
          flexDirection: "column"
        }}
      >
        <Route path={Routes.dealForm}>
          <Form onClose={goBack} />
        </Route>
        <Route path={[Routes.dealFile, Routes.dealNote]}>
          <Stack sx={{ flexDirection: "row", gap: "10px", margin: "16px 16px" }} justifyContent="flex-end">
            <IconButton onClick={goBack}>
              <CloseIcon />
            </IconButton>
          </Stack>
        </Route>
        <Route path={Routes.dealFile}>
          <FileMetadata />
        </Route>
        <Route path={Routes.dealNote}>
          <Notes />
        </Route>
        <Route path={Routes.dealDocument}>
          <Document onClose={goBack} />
        </Route>
      </Box>
    </Slide>
  );
};
